import { useState, useCallback, useRef } from 'react';

/**
 * Hook for managing confirmation dialog state.
 * Pairs with the existing ConfirmDialog component.
 *
 * Replaces the common pattern:
 *   const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });
 *
 * Usage:
 *   const confirm = useConfirmDialog();
 *   confirm.show({ title: 'Delete?', message: 'This cannot be undone.', onConfirm: handleDelete });
 *   <ConfirmDialog {...confirm.dialogProps} />
 */
const useConfirmDialog = () => {
  const [state, setState] = useState({
    open: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    confirmColor: 'error',
    onConfirm: null,
    onCancel: null,
  });

  // Callbacks live in refs, not read inside setState updaters: updaters must be pure, and React
  // StrictMode runs them twice in dev, which fired every confirmed action twice. firedRef also
  // stops a fast double-click from running the action twice in any build.
  const callbacksRef = useRef({ onConfirm: null, onCancel: null });
  const firedRef = useRef(false);

  /**
   * Show the confirmation dialog.
   * @param {object} options
   * @param {string} options.title - Dialog title
   * @param {string} options.message - Dialog message
   * @param {Function} options.onConfirm - Callback when confirmed
   * @param {Function} [options.onCancel] - Callback when declined/dismissed. Use when declining needs
   *   its own acknowledgement (e.g. confirming that nothing was changed), not just closing quietly.
   * @param {string} [options.confirmText='Confirm'] - Confirm button text
   * @param {string} [options.cancelText='Cancel'] - Cancel button text
   * @param {string} [options.confirmColor='error'] - MUI color for confirm button
   */
  const show = useCallback((options) => {
    callbacksRef.current = {
      onConfirm: options.onConfirm || null,
      onCancel: options.onCancel || null,
    };
    firedRef.current = false;
    setState({
      open: true,
      title: options.title || 'Confirm',
      message: options.message || 'Are you sure?',
      confirmText: options.confirmText || 'Confirm',
      cancelText: options.cancelText || 'Cancel',
      confirmColor: options.confirmColor || 'error',
      onConfirm: options.onConfirm || null,
      onCancel: options.onCancel || null,
    });
  }, []);

  // Closes without running the caller's onCancel — for programmatic dismissal.
  const close = useCallback(() => {
    setState((prev) => ({ ...prev, open: false }));
  }, []);

  // The user actively declined (Cancel button or backdrop dismiss).
  // Close first, then run the callback, so a show() from inside the callback (a follow-up dialog)
  // is not overwritten by the close.
  const handleCancel = useCallback(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    setState((prev) => ({ ...prev, open: false }));
    callbacksRef.current.onCancel?.();
  }, []);

  const handleConfirm = useCallback(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    setState((prev) => ({ ...prev, open: false }));
    callbacksRef.current.onConfirm?.();
  }, []);

  /**
   * Props to spread onto ConfirmDialog component.
   * Usage: <ConfirmDialog {...confirm.dialogProps} />
   */
  const dialogProps = {
    open: state.open,
    title: state.title,
    message: state.message,
    confirmText: state.confirmText,
    cancelText: state.cancelText,
    confirmColor: state.confirmColor,
    onConfirm: handleConfirm,
    onCancel: handleCancel,
  };

  return { show, close, dialogProps };
};

export default useConfirmDialog;
